import { Grid, Divider, Typography, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AddEditWorkedHours from "src/containers/MyCalendar/AddEditWorkedHours/AddEditWorkedHours";
import { ReportWorkedDays } from "src/containers/MyCalendar/ReportWorkedDays/ReportWorkedDays";
import { TnADayDataDTO } from "src/models/tna/tna-data/tna-day-data-dto";
import { TnAScheduleIntervalRef } from "src/models/tna/tna-schedule-intervals/tna-schedule-interval-ref";

import DayDataSummary from "./DayDataSummary";
import {
  Container,
  CalendarLikeContainer,
  CalendarLikeHeader,
  CalendarLikeBody,
  CalendarLikeAction,
  ClockInOutContainer,
  LiveClockContainer,
} from "./styles";
import { CalendarPlusIcon } from "../../../../assets/icons";
import { EmployeePresenceAndLeavesSummaryDTO } from "../../../../models/employee-presence-and-leaves-summary/employee-presence-and-leaves-summary-dto";

import { EmployeeRef } from "../../../../models/employees";
import { TnAConfigRef } from "../../../../models/tna";
import { TnALocationCodeDTO } from "../../../../models/tna-location-codes/tna-location-code-dto";

import { HOW_TO_REPORT_TIME, TNA_CLOCKING_TYPES } from "../../../../utils/constants/tna-constants";
import { computeMonthName } from "../../../../utils/time-utils";
import LiveClock from "../../../LiveClock/LiveClock";
import LoadingOverlay from "../../../LoadingOverlay/LoadingOverlay";
import RealTimeClockingButtons from "../../../RealTimeClockingButtons/RealTimeClockingButtons";

const weekDays = ["Monday", "Thuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const { ClockIn, ClockOut } = TNA_CLOCKING_TYPES;

interface State {
  saveLoading: boolean;
  error: string;
  actualTnAConfig: TnAConfigRef | null;
  todayIntervals: TnAScheduleIntervalRef[];
  employee: EmployeeRef | null;
  loading: boolean;
  clockingToSave: {
    date: Date;
    type: "clockIn" | "clockOut";
  } | null;
  isCCCDOpen: boolean;
  isWarningCCCDOpen: boolean;
  hoursBeforeClosestInterval: number | null;
  forPrevDay: boolean;
  tnALocationCodeOrName: string;
  tnALocationCode: TnALocationCodeDTO | null;
  locationWarning: boolean;
  isLocationCCCDOpen: boolean;
}

const MyTimesheetLink = ({
  customSummary,
  loading,
  reloadCustomSummary,
}: {
  customSummary: EmployeePresenceAndLeavesSummaryDTO;
  loading: boolean;
  reloadCustomSummary: () => void;
}) => {
  const { t } = useTranslation();
  const tRP = "components.custom_widget_links.my_presence_and_leaves.my_timesheet";
  const tRPActions = "components.my_clockings";
  const theme = useTheme();
  const [state, setState] = useState<State>({
    saveLoading: false,
    error: "",
    actualTnAConfig: null,
    todayIntervals: [],
    employee: null,
    loading: false,
    clockingToSave: null,
    isCCCDOpen: false,
    isWarningCCCDOpen: false,
    hoursBeforeClosestInterval: 0,
    forPrevDay: false,
    tnALocationCodeOrName: "",
    tnALocationCode: null,
    locationWarning: false,
    isLocationCCCDOpen: false,
  });

  //#region with zero date number
  const withZeroDateNumber = (number: number) => {
    if (number === 0) return "00";

    return number < 10 ? `0${number}` : `${number}`;
  };
  //#endregion

  useEffect(() => {
    setState((state) => ({
      ...state,
      actualTnAConfig:
        customSummary.tnAEmployeeConfig?.tnAConfig ??
        customSummary.tnAEmployeeConfig?.employeeGroupTnAConfig ??
        customSummary.tnAEmployeeConfig?.legalEntityTnAConfig ??
        customSummary.tnAEmployeeConfig?.companyAccountTnAConfig ??
        customSummary.tnAEmployeeConfig?.legislationTnAConfig ??
        customSummary.tnAEmployeeConfig?.allLegislationsTnAConfig ??
        null,
      employee: customSummary.tnAEmployeeConfig?.employee ?? null,
    }));
  }, [customSummary]);

  return (
    <>
      <Container>
        <Grid>
          <Typography style={{ color: theme.palette.primary.light }}>{`${t(`${tRP}.title`)}`}</Typography>
        </Grid>
        {loading ? (
          <Skeleton
            variant="rounded"
            width="100px"
            height={"100px"}
            style={{ marginLeft: "5px" }}
          />
        ) : (
          state.employee && (
            <Grid
              onClick={(e) => e.preventDefault()}
              style={{
                cursor: "default",
                position: "relative",
              }}
            >
              <LoadingOverlay
                loading={state.saveLoading}
                spinnerSize="2x"
                isSpinnerPositionAbsolute
                customSpinnerStyle={{
                  top: 50,
                  left: 0,
                }}
              >
                {state.actualTnAConfig?.how2ReportTime?.id === HOW_TO_REPORT_TIME.ClockingManually.id ? (
                  <ClockInOutContainer>
                    <Grid>
                      <Grid style={{ position: "absolute", right: "1px", top: "1px" }}>
                        {customSummary.tnAEmployeeDayData && (
                          <DayDataSummary
                            dayData={customSummary.tnAEmployeeDayData}
                            tnaConfig={state.actualTnAConfig}
                          />
                        )}
                      </Grid>
                      <LiveClockContainer>
                        <LiveClock />
                      </LiveClockContainer>
                      <Grid
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        {!state.actualTnAConfig.locationQRCodeIsMandatory && (
                          <RealTimeClockingButtons
                            employee={state.employee}
                            forPrevDay={state.forPrevDay}
                            tnATrackLocationId={state.actualTnAConfig?.tnATrackLocation?.id}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </ClockInOutContainer>
                ) : state.actualTnAConfig?.how2ReportTime?.id === HOW_TO_REPORT_TIME.AutoClockingBySytem.id ||
                  state.actualTnAConfig?.how2ReportTime?.id === HOW_TO_REPORT_TIME.AutoWorkedHours.id ||
                  state.actualTnAConfig?.how2ReportTime?.id === HOW_TO_REPORT_TIME.AutoWorkedDays.id ? (
                  <Grid
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: "12px",
                      padding: "8px",
                    }}
                  >
                    <Typography style={{ color: theme.palette.text.primary }}>
                      <b>{state.actualTnAConfig?.schedule?.code}</b>
                    </Typography>

                    {Number(state.actualTnAConfig?.schedule?.intervals.filter((interval) => interval)?.length) > 0 ? (
                      state.actualTnAConfig?.schedule?.intervals
                        .filter((interval) => interval.weekDay === new Date().getDay())
                        .sort((firstInterval, secondInterval) => firstInterval.startHour - secondInterval.startHour)
                        .map((interval) => (
                          <Typography key={interval.id}>
                            {withZeroDateNumber(interval.startHour)}:{withZeroDateNumber(interval.startMinute)} -{" "}
                            {withZeroDateNumber(interval.endHour)}:{withZeroDateNumber(interval.endMinute)}
                          </Typography>
                        ))
                    ) : (
                      <>{`${t(`${tRP}.no_interval`)}`} </>
                    )}
                  </Grid>
                ) : state.actualTnAConfig?.how2ReportTime?.id === HOW_TO_REPORT_TIME.ManualWorkedDays.id ? (
                  <CalendarLikeContainer>
                    <CalendarLikeHeader>
                      {customSummary.tnAEmployeeDayData && (
                        <Grid style={{ position: "absolute", top: "1px", right: "1px" }}>
                          <DayDataSummary
                            dayData={customSummary.tnAEmployeeDayData}
                            tnaConfig={state.actualTnAConfig}
                          />
                        </Grid>
                      )}
                      <Typography>{computeMonthName(new Date().getMonth())}</Typography>
                    </CalendarLikeHeader>
                    <CalendarLikeBody>
                      <Typography variant="h5">
                        <b>{new Date().getDate()}</b>
                      </Typography>
                      <Divider
                        flexItem
                        orientation="vertical"
                      />
                      <ReportWorkedDays
                        id={""}
                        employee={state.employee}
                        tnAMonth={{
                          year: new Date().getFullYear(),
                          month: new Date().getMonth(),
                        }}
                        dates={[new Date().getDate()]}
                        handleNotifyParent={reloadCustomSummary}
                        dayData={customSummary.tnAEmployeeDayData ? [customSummary.tnAEmployeeDayData] : []}
                      >
                        <CalendarLikeAction>
                          <CalendarPlusIcon />
                        </CalendarLikeAction>
                      </ReportWorkedDays>
                    </CalendarLikeBody>
                  </CalendarLikeContainer>
                ) : state.actualTnAConfig?.how2ReportTime?.id === HOW_TO_REPORT_TIME.ManualWorkedHours.id ? (
                  <>
                    <CalendarLikeContainer>
                      <CalendarLikeHeader>
                        {customSummary.tnAEmployeeDayData && (
                          <Grid style={{ position: "absolute", top: "1px", right: "1px" }}>
                            <DayDataSummary
                              dayData={customSummary.tnAEmployeeDayData}
                              tnaConfig={state.actualTnAConfig}
                            />
                          </Grid>
                        )}
                        <Typography>{computeMonthName(new Date().getMonth())}</Typography>
                      </CalendarLikeHeader>
                      <CalendarLikeBody>
                        <Typography variant="h5">
                          <b>{new Date().getDate()}</b>
                        </Typography>
                        <Divider
                          flexItem
                          orientation="vertical"
                        />
                        <AddEditWorkedHours
                          employee={state.employee}
                          tnADate={{
                            year: new Date().getFullYear(),
                            month: new Date().getMonth() + 1,
                            day: new Date().getDate(),
                          }}
                          notifyParent={reloadCustomSummary}
                          buttonType="icon"
                          dayData={[customSummary.tnAEmployeeDayData as TnADayDataDTO]}
                        />
                      </CalendarLikeBody>
                    </CalendarLikeContainer>
                  </>
                ) : (
                  <></>
                )}
              </LoadingOverlay>
            </Grid>
          )
        )}
      </Container>
    </>
  );
};
export default MyTimesheetLink;
